import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

type Pillar = 'processing' | 'storage' | 'transport' | 'wholesale' | 'retail';

interface Question {
  id: number;
  text: string;
  options: {
    label: string;
    description: string;
    mapsTo: Pillar;
  }[];
}

interface Result {
  pillar: Pillar;
  title: string;
  tagline: string;
  description: string;
  icon: string;
  href: string;
  ctaLabel: string;
  accentClass: string;
  bgClass: string;
  borderClass: string;
  iconBgClass: string;
}

const questions: Question[] = [
  {
    id: 1,
    text: 'What describes you best?',
    options: [
      {
        label: 'I own or manage cold storage facilities',
        description: 'Warehouse operator, cold store owner, or facility manager',
        mapsTo: 'storage',
      },
      {
        label: 'I own or operate refrigerated vehicles',
        description: 'Fleet owner, reefer transporter, or logistics provider',
        mapsTo: 'transport',
      },
      {
        label: 'I process, grade, or standardise frozen food products',
        description: 'Food processor, grader, packager, or quality controller',
        mapsTo: 'processing',
      },
      {
        label: 'I buy or sell frozen food in bulk quantities',
        description: 'Wholesale trader, processor, exporter, or institutional buyer',
        mapsTo: 'wholesale',
      },
      {
        label: 'I want quality frozen food delivered to my door',
        description: 'Home chef, family shopper, or individual consumer',
        mapsTo: 'retail',
      },
    ],
  },
  {
    id: 2,
    text: "What's your primary goal?",
    options: [
      {
        label: 'Maximize revenue from my idle assets',
        description: 'Turn empty freezer space and surplus capacity into earnings',
        mapsTo: 'storage',
      },
      {
        label: 'Get consistent loads and reduce deadhead trips',
        description: 'Keep my fleet moving and profitable every day',
        mapsTo: 'transport',
      },
      {
        label: 'Standardise my harvest into trade-ready SKUs',
        description: 'Clean, grade, weigh, pack — and sell at a premium with verified specs',
        mapsTo: 'processing',
      },
      {
        label: 'Source reliable, quality-verified frozen stock',
        description: 'Find consistent supply with transparent pricing and trust',
        mapsTo: 'wholesale',
      },
      {
        label: 'Convenient access to hygienic frozen food at fair prices',
        description: 'Skip the market — get standard-cut, frozen-fresh delivered',
        mapsTo: 'retail',
      },
    ],
  },
  {
    id: 3,
    text: "What's your biggest pain point right now?",
    options: [
      {
        label: 'My freezing capacity sits empty too often',
        description: 'Unpredictable demand and seasonal gaps hurt my bottom line',
        mapsTo: 'storage',
      },
      {
        label: 'Unpredictable demand and expensive empty return trips',
        description: 'Deadhead kilometres eat into my margins every month',
        mapsTo: 'transport',
      },
      {
        label: 'I lose value because my product isn\'t standardised or graded',
        description: "Buyers negotiate down because there's no trusted spec attached",
        mapsTo: 'processing',
      },
      {
        label: 'Inconsistent quality, opaque pricing, zero traceability',
        description: "I never know what grade I am actually buying until it arrives",
        mapsTo: 'wholesale',
      },
      {
        label: 'Limited access to quality frozen food in my area',
        description: 'The options near me are limited, inconsistent, or overpriced',
        mapsTo: 'retail',
      },
    ],
  },
];

const results: Record<Pillar, Result> = {
  processing: {
    pillar: 'processing',
    title: 'Freshhlock Processing',
    tagline: 'Standardise & Add Value',
    description:
      "You are a perfect fit for Freshhlock Processing. Bring your harvest to our network of processing centres — we clean, grade, weigh, vacuum-pack, and standardise it into a trade-ready SKU. With 42+ centres across coastal India and a 99.2% SKU conformance rate, your product commands better prices because buyers trust the spec.",
    icon: 'ri-barcode-line',
    href: '/processing',
    ctaLabel: 'Explore Processing',
    accentClass: 'text-accent-600',
    bgClass: 'bg-accent-50',
    borderClass: 'border-accent-200',
    iconBgClass: 'bg-accent-100 text-accent-600',
  },
  storage: {
    pillar: 'storage',
    title: 'Freshhlock Storage',
    tagline: 'Monetise Your Cold Storage',
    description:
      "You are a perfect fit for Freshhlock Storage. List your idle freezing capacity on India's largest cold-chain network, get matched with producers during harvest seasons, and turn empty space into a revenue stream. With 340+ warehouses already on the platform and real-time capacity mapping, your facility works harder — even during off-peak months.",
    icon: 'ri-building-2-line',
    href: '/storage',
    ctaLabel: 'Explore Storage',
    accentClass: 'text-primary-600',
    bgClass: 'bg-primary-50',
    borderClass: 'border-primary-200',
    iconBgClass: 'bg-primary-100 text-primary-600',
  },
  transport: {
    pillar: 'transport',
    title: 'Freshhlock Transport',
    tagline: 'Keep Your Fleet Moving',
    description:
      "You belong on Freshhlock Transport. List your reefer vehicles, get matched with real-time loads across India, and say goodbye to deadhead trips. With 2,500+ vehicles on the network, demand aggregation, and route-matching algorithms, your fleet stays productive — and profitable — every single day.",
    icon: 'ri-truck-line',
    href: '/transport',
    ctaLabel: 'Explore Transport',
    accentClass: 'text-secondary-600',
    bgClass: 'bg-secondary-50',
    borderClass: 'border-secondary-200',
    iconBgClass: 'bg-secondary-100 text-secondary-600',
  },
  wholesale: {
    pillar: 'wholesale',
    title: 'Freshhlock Wholesale',
    tagline: 'Trade With Confidence',
    description:
      "Freshhlock Wholesale is where you belong. Buy and sell frozen seafood, meat, and vegetables at trade scale with standard SKU specs, verified quality tiers, and full traceability. With 8,500+ SKUs traded monthly and a trust system that includes HACCP certification and export-grade verification, you finally know exactly what you are buying.",
    icon: 'ri-shopping-bag-3-line',
    href: '/wholesale',
    ctaLabel: 'Explore Wholesale',
    accentClass: 'text-accent-600',
    bgClass: 'bg-accent-50',
    borderClass: 'border-accent-200',
    iconBgClass: 'bg-accent-100 text-accent-600',
  },
  retail: {
    pillar: 'retail',
    title: 'Freshhlock Retail',
    tagline: 'Quality Frozen Food, Delivered',
    description:
      "Freshhlock Retail is built for you. Order standard-cut, hygienic frozen seafood, meat, and vegetables from your phone — delivered cold, every time. With 12,000+ orders delivered monthly, transparent pricing, and temperature-verified last-mile logistics, you get restaurant-quality frozen food without the hassle.",
    icon: 'ri-store-2-line',
    href: '/retail',
    ctaLabel: 'Explore Retail',
    accentClass: 'text-accent-600',
    bgClass: 'bg-accent-50',
    borderClass: 'border-accent-200',
    iconBgClass: 'bg-accent-100 text-accent-600',
  },
};

const PILLAR_ORDER: Pillar[] = ['processing', 'storage', 'transport', 'wholesale', 'retail'];

export default function QuizWidget() {
  const [step, setStep] = useState<'intro' | number | 'result'>('intro');
  const [scores, setScores] = useState<Record<Pillar, number>>({
    processing: 0,
    storage: 0,
    transport: 0,
    wholesale: 0,
    retail: 0,
  });
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);
  const [copied, setCopied] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  const totalQuestions = questions.length;

  const handleAnswer = useCallback(
    (mapsTo: Pillar, optionIndex: number) => {
      if (animating) return;
      setSelectedOption(optionIndex);
      setAnimating(true);

      setTimeout(() => {
        setScores((prev) => ({ ...prev, [mapsTo]: prev[mapsTo] + 1 }));
        const currentQ = typeof step === 'number' ? step : 0;
        const nextQ = currentQ + 1;

        if (nextQ >= questions.length) {
          setStep('result');
        } else {
          setStep(nextQ);
        }
        setSelectedOption(null);
        setAnimating(false);
      }, 400);
    },
    [step, animating]
  );

  const getResult = (): Result => {
    let winner: Pillar = 'storage';
    let maxScore = -1;
    // Deterministic tie-break: first match in PILLAR_ORDER wins
    PILLAR_ORDER.forEach((p) => {
      if (scores[p] > maxScore) {
        maxScore = scores[p];
        winner = p;
      }
    });
    return results[winner];
  };

  const getRunnerUps = (): Result[] => {
    const result = getResult();
    return PILLAR_ORDER.filter((p) => p !== result.pillar)
      .map((p) => results[p])
      .sort((a, b) => scores[b.pillar] - scores[a.pillar]);
  };

  const resetQuiz = () => {
    setStep('intro');
    setScores({ processing: 0, storage: 0, transport: 0, wholesale: 0, retail: 0 });
    setSelectedOption(null);
    setAnimating(false);
  };

  const startQuiz = () => {
    setStep(0);
  };

  const handleShare = async () => {
    const result = getResult();
    const text = `I took the Freshhlock quiz and got matched with ${result.title} — ${result.tagline}. Find your pillar at ${window.location.origin}/quiz`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My Freshhlock Match', text, url: window.location.href });
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // clipboard denied
      }
    }
  };

  // Auto-scroll to top of widget on step change
  useEffect(() => {
    if (widgetRef.current && (typeof step === 'number' || step === 'result')) {
      widgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [step]);

  const result = getResult();
  const runnerUps = getRunnerUps();

  return (
    <div ref={widgetRef} className="w-full max-w-2xl mx-auto">
      {/* Intro Screen */}
      {step === 'intro' && (
        <div className="text-center animate-fade-in-up">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-accent-100 text-primary-600">
            <i className="ri-compass-3-line text-3xl"></i>
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-primary-700 mb-3">
            Which Freshhlock Is Right for You?
          </h3>
          <p className="text-foreground-500 max-w-md mx-auto mb-8 text-sm md:text-base leading-relaxed">
            Answer three quick questions and we&apos;ll point you to the Freshhlock pillar that fits your needs — processing, storage, transport, wholesale, or retail.
          </p>
          <button
            onClick={startQuiz}
            className="px-10 py-3.5 bg-brand-500 hover:bg-brand-600 text-paper-50 font-semibold text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
          >
            Start the Quiz
          </button>
          <p className="text-xs text-foreground-400 mt-4">Takes less than a minute</p>
        </div>
      )}

      {/* Question Screens */}
      {typeof step === 'number' && (
        <div className="animate-fade-in-up">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                Question {step + 1} of {totalQuestions}
              </span>
              <span className="text-xs text-foreground-400">
                {Math.round(((step + 1) / totalQuestions) * 100)}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-brand-500 to-accent-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((step + 1) / totalQuestions) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question */}
          <h3 className="font-heading text-lg md:text-xl font-bold text-primary-700 mb-6 text-center">
            {questions[step].text}
          </h3>

          {/* Options */}
          <div className="space-y-3">
            {questions[step].options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(option.mapsTo, idx)}
                disabled={animating}
                className={`w-full text-left p-4 md:p-5 rounded-xl border transition-all duration-300 cursor-pointer ${
                  selectedOption === idx
                    ? 'border-primary-400 bg-primary-50 shadow-[0_0_0_2px_rgba(11,37,69,0.08)] scale-[0.98]'
                    : 'border-background-200 bg-background-50 hover:border-primary-200 hover:bg-background-100'
                } ${animating ? 'pointer-events-none' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 transition-colors text-sm font-semibold ${
                      selectedOption === idx
                        ? 'bg-brand-500 text-paper-50'
                        : 'bg-background-100 text-foreground-400'
                    }`}
                  >
                    {String.fromCharCode(65 + idx)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground-900 text-sm md:text-base mb-1">
                      {option.label}
                    </div>
                    <div className="text-xs text-foreground-400">{option.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Result Screen */}
      {step === 'result' && (
        <div className="animate-fade-in-up">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 border border-accent-200 mb-5">
              <i className="ri-sparkling-2-fill text-accent-600 text-xs"></i>
              <span className="text-xs font-semibold text-accent-700 uppercase tracking-wider">
                Your Match
              </span>
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-primary-700 mb-2">
              {result.tagline}
            </h3>
            <p className="text-foreground-500 text-sm">{result.title}</p>
          </div>

          {/* Main result card */}
          <div
            className={`rounded-xl border ${result.borderClass} ${result.bgClass} p-6 md:p-8 mb-6 shadow-sm`}
          >
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div
                className={`w-14 h-14 flex items-center justify-center rounded-xl shrink-0 ${result.iconBgClass}`}
              >
                <i className={`${result.icon} text-2xl`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  {result.description}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={result.href}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-500 hover:bg-brand-600 text-paper-50 font-semibold text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    {result.ctaLabel}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 py-3 border border-background-200 hover:border-primary-300 text-foreground-600 hover:text-primary-600 font-medium text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className={`${copied ? 'ri-check-line' : 'ri-share-line'}`}></i>
                    {copied ? 'Link Copied!' : 'Share Result'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Score breakdown */}
          <div className="bg-card-50 border border-card-200 rounded-xl p-5 mb-8 shadow-sm">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
              How You Scored
            </h4>
            <div className="space-y-3">
              {PILLAR_ORDER.map((p) => {
                const pct = Math.round((scores[p] / totalQuestions) * 100);
                return (
                  <div key={p} className="flex items-center gap-3">
                    <span className="w-20 text-xs font-medium text-foreground-600 capitalize shrink-0">
                      {p}
                    </span>
                    <div className="flex-1 h-2 bg-background-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          p === result.pillar
                            ? 'bg-gradient-to-r from-brand-500 to-accent-500'
                            : 'bg-background-300'
                        }`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                    <span className="w-8 text-right text-xs font-semibold text-foreground-700">
                      {scores[p]}/{totalQuestions}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Runner-up pillars */}
          <div className="mb-8">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
              You might also like
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {runnerUps.slice(0, 2).map((r) => (
                <Link
                  key={r.pillar}
                  to={r.href}
                  className={`flex items-center gap-3 p-4 rounded-xl border ${r.borderClass} ${r.bgClass} shadow-sm hover:shadow-md transition-all group`}
                >
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 ${r.iconBgClass}`}
                  >
                    <i className={`${r.icon} text-lg`}></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground-800 group-hover:text-foreground-900 transition-colors">
                      {r.title}
                    </div>
                    <div className="text-xs text-foreground-500">{r.tagline}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Retry */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={resetQuiz}
              className="px-6 py-2.5 border border-background-200 hover:border-primary-300 text-foreground-600 hover:text-primary-600 font-medium text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line mr-1.5"></i>
              Retake Quiz
            </button>
            <span className="text-xs text-foreground-400">
              Not sure? Explore all five pillars on the homepage.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}